// The per-shop knob WIRE SHAPE — one table, both directions, one owner.
//
// `/admin/shops/{id}/knobs/shop` accepts a FLAT map of primitives only (string|number|boolean|null,
// ≤64 keys, PUT is wholesale-replace). The Studio and the composer both work in a NESTED shape —
// `wallMaterial: { preset, tint }` — because that is what a picker binds to and what the resolver
// reads. Something has to translate, in both directions.
//
// 🔴 ASK-129 §E2E102 F1: only ONE direction had ever been written. `foldShopKnobs` (wire → nested)
// lived here in spirit and in template-composition.js in fact; nothing did nested → wire. The writer
// took `knobs.wallMaterial`, saw an OBJECT, and the primitive filter dropped it silently — so every
// material a merchant chose was discarded on the way out while `shopName`, `brightness` and
// `shadowIntensity` (already primitives) went through. That is exactly the row Vanessa's E2E102 run
// left behind, measured 2026-09-09:
//
//     GET /admin/shops/spider-web-comics-test101b-2/knobs/shop
//     → {"shopName":"Comics","brightness":2.35,"shadowIntensity":5}
//
// Three scalars present, every object-valued knob absent. The allowlist was never the problem — it
// already names `wallTint`/`floorTint`/… precisely because the wire is flat. The flattening step
// those keys exist for was missing.
//
// Both directions now live in this one module, so a new knob is added to ONE table and the round
// trip cannot go half-built again. Pure (no DOM, no fetch) — `test-shop-knob-wire` round-trips it.

// Material knobs split across two flat keys: the preset id and its tint. `lightingRecipe` has no
// tint pair. Adding a material knob means adding a row here and nothing else.
export const MATERIAL_TINT_PAIRS = Object.freeze([
	{ material: "wallMaterial",     tint: "wallTint"     },
	{ material: "floorMaterial",    tint: "floorTint"    },
	{ material: "ceilingTreatment", tint: "ceilingTint"  },
	{ material: "fixtureMaterial",  tint: "fixtureTint"  },
	{ material: "counterMaterial",  tint: "counterTint"  },
	{ material: "signMode",         tint: "signTint"     },
	{ material: "lightingRecipe",   tint: null           },
]);

// Knobs that are already scalars on both sides and need no reshaping.
// shopName (2026-06-11): the sign renderer reads knobs.shopName first.
// accentColor: ASK-129 F1 — it was on the READ list and missing from the WRITE allowlist, so an
// accent save could never persist however correct the rest of the path was.
export const SHOP_SCALAR_KEYS = Object.freeze([
	"brightness", "accentColor", "signLogoUrl", "shopName", "shadowIntensity",
]);

// Flat key families the wire carries verbatim: per-zone moods, per-instance material overrides
// (ASK-086), per-kind fixture finishes (Amendment-3). Already primitives, already flat.
export const SHOP_KNOB_PREFIXES = Object.freeze(["zoneLight.", "material.", "fixtureMaterial."]);

/**
 * WIRE → NESTED. Fold the flat `.shop` primitive map into the authored top-level knobs.
 * Merchant-edited values override published-scene defaults; null/absent in the map means "no
 * override" and falls through to authored. Returns a new object — does not mutate inputs.
 */
export function foldShopKnobs(authored, shop) {
	if (!shop || typeof shop !== "object") return authored;
	const out = { ...authored };

	for (const { material, tint } of MATERIAL_TINT_PAIRS) {
		const matVal = shop[material];
		if (matVal == null) continue;                        // null/undefined → no override
		const shopTint     = tint ? shop[tint] : undefined;
		const authoredTint = out[material]?.tint;
		const finalTint    = shopTint != null ? shopTint : authoredTint;
		out[material] = finalTint ? { preset: matVal, tint: finalTint } : { preset: matVal };
	}
	for (const key of SHOP_SCALAR_KEYS) {
		if (shop[key] != null) out[key] = shop[key];
	}
	// Flat families need no reshaping — but they DO need carrying across, or this is not the inverse
	// of `flattenShopKnobs` and the round trip loses them. Caught by `test-shop-knob-wire`'s
	// flatten→fold→flatten leg against the first cut of this module: per-zone moods, per-instance
	// material overrides and per-kind fixture finishes all fell out of a hydrate. It also means a
	// merchant's zone mood now survives the bake into the composer, which it never did — the fold
	// simply ignored those keys in `.shop`.
	for (const [key, value] of Object.entries(shop)) {
		if (value == null) continue;
		if (SHOP_KNOB_PREFIXES.some((p) => key.startsWith(p))) out[key] = value;
	}
	return out;
}

/**
 * NESTED → WIRE. Reshape the Studio's knob map into the flat primitive map the endpoint accepts.
 *
 * `{ preset, tint }` becomes `<material>` + `<material>Tint`. A knob already in wire shape (a bare
 * preset string, as it arrives from a hydrate) passes through unchanged, so this is idempotent.
 *
 * The tint key is EMITTED ONLY WHEN THERE IS A TINT, deliberately: PUT is wholesale-replace, so
 * omitting it removes the server's copy, and `foldShopKnobs` then falls back to the authored tint —
 * which is what the Studio's ∅ chip means ("no tint override"), not "force untinted".
 *
 * Non-primitives that survive nothing here are dropped rather than sent, so we never rely on the
 * server's silent-drop for routine state.
 */
export function flattenShopKnobs(knobs) {
	const out = {};
	const isPrimitive = (v) => {
		const t = typeof v;
		return t === "string" || t === "number" || t === "boolean" || v === null;
	};
	const materialKeys = new Set(MATERIAL_TINT_PAIRS.map((p) => p.material));

	for (const { material, tint } of MATERIAL_TINT_PAIRS) {
		const val = knobs?.[material];
		if (val == null) continue;
		if (typeof val === "string") { out[material] = val; continue; }   // already wire-shaped
		if (typeof val !== "object") continue;
		if (typeof val.preset === "string") out[material] = val.preset;
		if (tint && typeof val.tint === "string" && val.tint) out[tint] = val.tint;
	}
	for (const key of SHOP_SCALAR_KEYS) {
		const v = knobs?.[key];
		// 🔴 ASK-129 §E2E102-rerun item 2 — AN EMPTY STRING IS NOT A VALUE, it is the absence of one,
		// and the wire says absence by omitting the key. Measured on Vanessa's shop: `"shopName": ""`
		// reached both the GCG row and the boot. The render survived only because the resolver treats
		// it as falsy and falls through; anything that read it literally would have printed a blank
		// sign. Omitting instead means wholesale-replace clears the stale `""` on the next save, so a
		// row already carrying one heals itself rather than needing a migration.
		if (typeof v === "string" && v.trim() === "") continue;
		if (isPrimitive(v)) out[key] = v;
	}
	// Flat families pass through verbatim. `material.`-prefixed keys must not be mistaken for the
	// `fixtureMaterial` material knob handled above, so the exact material keys are excluded.
	for (const [key, value] of Object.entries(knobs || {})) {
		if (materialKeys.has(key) || SHOP_SCALAR_KEYS.includes(key)) continue;
		if (!SHOP_KNOB_PREFIXES.some((p) => key.startsWith(p))) continue;
		if (isPrimitive(value)) out[key] = value;
	}
	return out;
}
